import ApiClient from "../apiClient";

export type ScoutProvider = "nous" | "openai" | "anthropic" | "moonshot";
export type ScoutStatus = "draft" | "active" | "paused" | "disabled";
export type ScoutOutcomeState =
  "informational" | "actionable" | "completed" | "failed" | "unavailable";
export type ScoutTriggerType =
  | "manual"
  | "schedule"
  | "desk_plan_any"
  | "desk_plan_miss"
  | "desk_plan_beat"
  | "risk_signal_new"
  | "risk_signal_keyword";

export interface ScoutTriggerConfig {
  type: ScoutTriggerType;
  enabled: boolean;
  schedule?: string | null;
  timezone?: string | null;
  keywords?: string[];
  cursor?: Record<string, unknown>;
}

export interface ScoutToolPolicyInput {
  toolName: string;
  scope: string;
  inputConstraints: Record<string, unknown>;
  riskLevel: "low" | "medium" | "high";
}

export interface ScoutToolPolicy extends ScoutToolPolicyInput {
  id: string;
  scoutId: string;
  userId: string;
  status: "active" | "revoked";
  approvedAt: string | null;
  createdAt: string;
}

export interface ScoutRecord {
  id: string;
  userId: string;
  name: string;
  instructions: string;
  status: ScoutStatus;
  provider: ScoutProvider;
  model: string | null;
  conversationId: string | null;
  triggerConfig: ScoutTriggerConfig[];
  mobileBridge: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  toolPolicies: ScoutToolPolicy[];
}

export interface ScoutRun {
  id: string;
  scoutId: string;
  userId: string;
  status:
    | "queued"
    | "running"
    | "approval_needed"
    | "succeeded"
    | "failed"
    | "cancelled";
  triggerType: ScoutTriggerType;
  triggerEventId: string | null;
  provider: ScoutProvider;
  model: string | null;
  output: string | null;
  error: string | null;
  metadata: Record<string, unknown>;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  outcome: ScoutRunOutcome;
}

export interface ScoutOutcomeAction {
  kind: "authorize_and_rerun";
  label: string;
  stepId: string;
  toolName: string;
}

export interface ScoutRunOutcome {
  state: ScoutOutcomeState;
  title: string;
  summary: string;
  action: ScoutOutcomeAction | null;
  recordedAt: string;
}

export interface ScoutRunStep {
  id: string;
  runId: string;
  stepIndex: number;
  kind: string;
  title: string;
  detail: string | null;
  toolName: string | null;
  toolInput: Record<string, unknown> | null;
  decision: string | null;
  createdAt: string;
}

export interface ScoutRunWithSteps extends ScoutRun {
  steps: ScoutRunStep[];
}

export interface ScoutProviderStatus {
  provider: ScoutProvider;
  label: string;
  configured: boolean;
  detail: string;
  model: string | null;
}

export interface ScoutService {
  list(): Promise<ScoutRecord[]>;
  providerStatus(): Promise<ScoutProviderStatus[]>;
  create(input: Partial<ScoutRecord>): Promise<ScoutRecord>;
  update(id: string, input: Partial<ScoutRecord>): Promise<ScoutRecord>;
  disable(id: string): Promise<ScoutRecord>;
  run(
    id: string,
    metadata?: Record<string, unknown>,
  ): Promise<ScoutRunWithSteps>;
  listRuns(id: string): Promise<ScoutRun[]>;
  getRun(scoutId: string, runId: string): Promise<ScoutRunWithSteps>;
  draftFromConversation(input: {
    conversationId: string;
    provider: ScoutProvider;
    model?: string | null;
  }): Promise<ScoutRecord>;
}

export function createScoutService(client: ApiClient): ScoutService {
  return {
    async list() {
      const json = await client.get<{ scouts: ScoutRecord[] }>("/api/scouts");
      return json.scouts ?? [];
    },
    async providerStatus() {
      const json = await client.get<{ providers: ScoutProviderStatus[] }>(
        "/api/scouts/provider-status",
      );
      return json.providers ?? [];
    },
    async create(input) {
      const json = await client.post<{ scout: ScoutRecord }>(
        "/api/scouts",
        input,
      );
      return json.scout;
    },
    async update(id, input) {
      const json = await client.patch<{ scout: ScoutRecord }>(
        `/api/scouts/${id}`,
        toScoutPayload(input),
      );
      return json.scout;
    },
    async disable(id) {
      const json = await client.delete<{ scout: ScoutRecord }>(
        `/api/scouts/${id}`,
      );
      return json.scout;
    },
    async run(id, metadata = {}) {
      const json = await client.post<{ run: ScoutRunWithSteps }>(
        `/api/scouts/${id}/run`,
        { triggerType: "manual", metadata },
      );
      return json.run;
    },
    async listRuns(id) {
      const json = await client.get<{ runs: ScoutRun[] }>(
        `/api/scouts/${id}/runs`,
      );
      return json.runs ?? [];
    },
    async getRun(scoutId, runId) {
      const json = await client.get<{ run: ScoutRunWithSteps }>(
        `/api/scouts/${scoutId}/runs/${runId}`,
      );
      return json.run;
    },
    async draftFromConversation(input) {
      const json = await client.post<{ scout: ScoutRecord }>(
        "/api/scouts/draft/from-conversation",
        input,
      );
      return json.scout;
    },
  };
}

function toScoutPayload(input: Partial<ScoutRecord>): Record<string, unknown> {
  return {
    ...input,
    toolPolicies: input.toolPolicies?.map((policy) => ({
      toolName: policy.toolName,
      scope: policy.scope,
      inputConstraints: policy.inputConstraints,
      riskLevel: policy.riskLevel,
    })),
  };
}
